// Wire frames for the cross-host `host-transfer` mode.
//
// After a `mode: "host-transfer"` hello is accepted, file bytes stream as JSON
// frames over the encrypted relay channel (one frame per
// `ch.send(JSON.stringify(frame))`), in whichever direction the op dictates:
//
//   op "get"  — accept side reads `path` and streams chunks TO the dialer.
//   op "put"  — dialer streams chunks TO the accept side, which writes `path`.
//
// Chunk payloads are base64 so arbitrary bytes survive the JSON round-trip.
// Chunk size is bounded well under Cloudflare's 1 MiB Durable-Object WebSocket
// message cap: raw 192 KiB → base64 ≈ 256 KiB → JSON frame → encrypted +
// base64 again ≈ 350 KiB on the wire.
//
// Flow control: the receiver acks every `HOST_TRANSFER_ACK_EVERY` chunks with
// the highest contiguous seq it has consumed; the sender keeps at most
// `HOST_TRANSFER_WINDOW_CHUNKS` unacked chunks in flight. This bounds memory
// on both daemons and in the relay regardless of file size.
//
// Integrity: the sender announces size + sha256 up front (`meta` for get, the
// hello's `transfer` payload for put) and the receiver verifies the digest of
// the assembled file before declaring success.

import { z } from "zod";

export const hostTransferFrameSchema = z.discriminatedUnion("type", [
  /** get: accept → dialer, before the first chunk. */
  z.object({
    type: z.literal("meta"),
    size: z.number().int().nonnegative(),
    sha256: z.string(),
  }),
  /** put: accept → dialer, once the destination is writable. */
  z.object({ type: z.literal("ready") }),
  /** File bytes, in order. `seq` starts at 0. */
  z.object({
    type: z.literal("chunk"),
    seq: z.number().int().nonnegative(),
    dataB64: z.string(),
  }),
  /** Receiver → sender: highest contiguous chunk seq consumed so far. */
  z.object({ type: z.literal("ack"), seq: z.number().int().nonnegative() }),
  /** Sender → receiver: all chunks sent. */
  z.object({ type: z.literal("done"), chunks: z.number().int().nonnegative() }),
  /** Receiver → sender: file assembled and sha256 verified. Terminal. */
  z.object({ type: z.literal("ok"), bytes: z.number().int().nonnegative() }),
  /** Either side: fatal failure. Terminal. */
  z.object({ type: z.literal("error"), message: z.string() }),
]);

export type HostTransferFrame = z.infer<typeof hostTransferFrameSchema>;

/** Raw bytes per chunk frame (before base64). */
export const HOST_TRANSFER_CHUNK_BYTES = 192 * 1024;

/** Receiver acks after every N consumed chunks (and always on the last one). */
export const HOST_TRANSFER_ACK_EVERY = 16;

/** Max unacked chunks the sender keeps in flight (~12 MiB raw). */
export const HOST_TRANSFER_WINDOW_CHUNKS = 64;

/** Hard cap on a single transferred file. */
export const HOST_TRANSFER_MAX_FILE_BYTES = 256 * 1024 * 1024; // 256 MiB

/** Abort if no frame moves in either direction for this long. */
export const HOST_TRANSFER_IDLE_TIMEOUT_MS = 60_000;

/** Absolute ceiling on one transfer, however slow the link. */
export const HOST_TRANSFER_MAX_DURATION_MS = 30 * 60 * 1000; // 30 min
